import { create } from 'zustand';
import type { ClientWithPortal, PortalFile, PortalActivity } from '@affogato/shared';

export interface ClientDetail extends ClientWithPortal {
  portalFiles: PortalFile[];
  activities: PortalActivity[];
}

interface ClientState {
  clients: ClientWithPortal[];
  currentClient: ClientDetail | null;
  isLoading: boolean;
  searchQuery: string;

  // Actions
  setClients: (clients: ClientWithPortal[]) => void;
  addClient: (client: ClientWithPortal) => void;
  updateClient: (id: string, updates: Partial<ClientWithPortal>) => void;
  removeClient: (id: string) => void;
  setCurrentClient: (client: ClientDetail | null) => void;
  updateCurrentClientPortal: (updates: Partial<ClientDetail['portal']>) => void;
  addPortalFile: (file: PortalFile) => void;
  removePortalFile: (portalFileId: string) => void;
  setPortalFiles: (files: PortalFile[]) => void;
  addActivity: (activity: PortalActivity) => void;
  setIsLoading: (loading: boolean) => void;
  setSearchQuery: (query: string) => void;
}

export const useClientStore = create<ClientState>((set) => ({
  clients: [],
  currentClient: null,
  isLoading: false,
  searchQuery: '',

  setClients: (clients) => set({ clients }),

  addClient: (client) =>
    set((state) => ({
      clients: [client, ...state.clients],
    })),

  updateClient: (id, updates) =>
    set((state) => ({
      clients: state.clients.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
      currentClient:
        state.currentClient?.id === id
          ? { ...state.currentClient, ...updates }
          : state.currentClient,
    })),

  removeClient: (id) =>
    set((state) => ({
      clients: state.clients.filter((c) => c.id !== id),
      currentClient: state.currentClient?.id === id ? null : state.currentClient,
    })),

  setCurrentClient: (client) => set({ currentClient: client }),

  updateCurrentClientPortal: (updates) =>
    set((state) => {
      if (!state.currentClient) return state;
      return {
        currentClient: {
          ...state.currentClient,
          portal: state.currentClient.portal
            ? { ...state.currentClient.portal, ...updates }
            : null,
        },
      };
    }),

  addPortalFile: (file) =>
    set((state) => {
      if (!state.currentClient) return state;
      return {
        currentClient: {
          ...state.currentClient,
          portalFiles: [...state.currentClient.portalFiles, file],
        },
      };
    }),

  removePortalFile: (portalFileId) =>
    set((state) => {
      if (!state.currentClient) return state;
      return {
        currentClient: {
          ...state.currentClient,
          portalFiles: state.currentClient.portalFiles.filter(
            (f) => f.id !== portalFileId
          ),
        },
      };
    }),

  setPortalFiles: (files) =>
    set((state) => {
      if (!state.currentClient) return state;
      return {
        currentClient: {
          ...state.currentClient,
          portalFiles: files,
        },
      };
    }),

  addActivity: (activity) =>
    set((state) => {
      if (!state.currentClient) return state;
      return {
        currentClient: {
          ...state.currentClient,
          activities: [activity, ...state.currentClient.activities],
        },
      };
    }),

  setIsLoading: (isLoading) => set({ isLoading }),

  setSearchQuery: (searchQuery) => set({ searchQuery }),
}));
